/* eslint-disable id-length */
import moment from "moment";
import { name } from "./__init__";
import { createSelector } from "reselect";
import { fromJS, List, Map } from "immutable";
import { selectors as appSelectors } from "../../containers/App";
import {
  buildImgUrl,
  calculateServiceRate,
  customFormObjectToString,
  getExchangeMultiplier,
  sortBy,
  transformBookableWithCurrencyConversion,
} from "../../utils/misc";
import constants from "../../utils/constants";
import { calculateDaysBetweenDates } from "../../utils/date";
import { currencyExchangeSelector, propertyTickerSelector } from "../ReservationManager/selectors";
import {
  bookingFormFieldsSelectorInput,
  occupantsSelectorRangeSelector,
  showChildrenCounter,
  childrenCountSelector,
} from "../BookingFormWrapper/selectors";
import { calculateSelectorsRange } from "../../utils/bookingFormUtils";
import {
  buildPropertyList,
  deviceInfoSelector,
  getCancellationPoliciesInfo,
  getCancellationRestrictions,
  occupantRestrictionSelector,
} from "../App/selectors";
import { CHILDREN_COUNT_FIELD } from "../../components/BookingForm/constants";

const selectors = appSelectors;

export const getModelSelector = state => state[name];
export const propsSelector = (_, props) => props;

export const isLoadingSelector = createSelector(
  getModelSelector,
  model => model && model.get("loading")
);

export const isModificationSuccessSelector = createSelector(
  getModelSelector,
  model => model && model.get("modificationSuccess")
);

function filterInventoriesByCondition(
  propertyTicker,
  accommodationARI,
  modificationPolicy,
  currentCondition
) {
  const canChangeConditions = modificationPolicy && modificationPolicy.get("canChangeConditions");
  if (canChangeConditions) {
    return accommodationARI;
  }
  const accommodationARIObj = accommodationARI.toJS()[propertyTicker];
  //filter conditions
  if (!accommodationARIObj.conditions[currentCondition]) {
    accommodationARIObj.conditions = {};
    accommodationARIObj.ariValues = {};
    accommodationARIObj.accommodations = {};
    accommodationARIObj.inventories = {};
    return fromJS(accommodationARIObj);
  }

  // get list of valid inventoryTickers (those that match the original roomStay condition)
  const validInventoryTickers = Object.values(accommodationARIObj.inventories)
    .filter(inventory => inventory.condition === currentCondition)
    .map(it => it.ticker);

  // get list of invalid inventoryTickers (those that do not match the original roomStay condition)
  const invalidInventoryTickers = Object.values(accommodationARIObj.inventories)
    .filter(inventory => inventory.condition !== currentCondition)
    .map(it => it.ticker);

  //filter inventories that match roomStay condition
  const filteredEntries = Object.entries(accommodationARIObj.inventories).filter(
    ([inventoryTicker, inventory]) => inventory.condition === currentCondition
  );
  // re-assign in the ari obj the inventories to skip invalid ones
  accommodationARIObj.inventories = Object.fromEntries(filteredEntries);

  //Accommodations have a list on inventories associated with them
  //we only keep the previously valid inventories ignoring the rest
  Object.values(accommodationARIObj.accommodations).forEach(accommodation => {
    accommodation.inventories = accommodation.inventories.filter(it =>
      validInventoryTickers.includes(it)
    );
  });

  // get list of invalid invalidAccommodationTickers, those that only have invalid inventories
  // since they are empty, we must no longer offer them
  const invalidAccommodationTickers = Object.values(accommodationARIObj.accommodations)
    .filter(accommodation => !accommodation.inventories.length)
    .map(it => it.ticker);

  // same as above, only keep accommodations that have at least one inventory
  const filteredAccEntries = Object.entries(accommodationARIObj.accommodations).filter(
    ([accommodationTicker, accommodation]) => accommodation.inventories.length
  );
  // re-assign in the ari obj the accommodations to skip invalid ones
  accommodationARIObj.accommodations = Object.fromEntries(filteredAccEntries);

  //filter arivalues, remove all accommodation and inventory keys that are invalid
  const invalidAriValueKeys = Object.keys(accommodationARIObj.arivalues)
    .filter(it => invalidAccommodationTickers.includes(it.replace("accommodation_", "")))
    .concat(
      Object.keys(accommodationARIObj.arivalues).filter(it => invalidInventoryTickers.includes(it))
    );
  invalidAriValueKeys.forEach(key => {
    delete accommodationARIObj.arivalues[key];
  });
  return fromJS({ [propertyTicker]: accommodationARIObj });
}

export const modificationPoliciesSelector = createSelector(
  selectors.propertyDataSelector,
  selectors.propertyTickerSelector,
  (propertyData, propertyTicker) => {
    if (!propertyData) {
      return undefined;
    }
    return propertyData.get(propertyTicker).get("modificationPolicies");
  }
);

export const roomStaySelector = createSelector(
  selectors.reservationSelector,
  propsSelector,
  (reservation, props) => {
    if (!props.params || !props.params.reservationId || props.params.reservationId[0] === "M") {
      return reservation.get("roomReservations").get(0);
    }
    return reservation
      .get("roomReservations")
      .filter(it => it.get("confirmationId") === props.params.reservationId)
      .get(0);
  }
);

export const currentConditionSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("conditionTicker")
);

export const modificationPolicySelector = createSelector(
  modificationPoliciesSelector,
  currentConditionSelector,
  (modificationPolicies, currentCondition) => {
    if (!currentCondition || !modificationPolicies) {
      return undefined;
    }
    return modificationPolicies
      .valueSeq()
      .filter(it => it.get("conditionRates").indexOf(currentCondition) >= 0)
      .find(it => it);
  }
);

export const accommodationARISelector = createSelector(
  getModelSelector,
  modificationPolicySelector,
  currentConditionSelector,
  selectors.propertyTickerSelector,

  (model, modificationPolicy, currentCondition, propertyTicker) => {
    let accommodationARI = model && model.get("accommodationARI");
    if (!accommodationARI) {
      return;
    }
    accommodationARI = filterInventoriesByCondition(
      propertyTicker,
      accommodationARI,
      modificationPolicy,
      currentCondition
    );
    return accommodationARI;
  }
);

export const reservationARISelector = createSelector(
  getModelSelector,
  model => model && model.get("reservationARI")
);

export const accommodationSelector = createSelector(
  getModelSelector,
  model => model && model.get("accommodation")
);

export const reservationCurrencySelector = createSelector(
  selectors.reservationSelector,
  reservation =>
    reservation && reservation.getIn(["roomReservations", "0", "totalAmount", "currency"])
);

export const exchangeMultiplierSelector = createSelector(
  selectors.reservationSelector,
  selectors.currencySelector,
  currencyExchangeSelector,
  reservationCurrencySelector,
  (reservation, currency, currencyExchange, reservationCurrency) =>
    getExchangeMultiplier(reservationCurrency, currency, currencyExchange)
);

export const roomStayIdSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.get("id")
);

export const roomStayConfirmationIdSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.getIn(["roomReservations", "0", "confirmationId"])
);

export const serviceRatesInfoSelector = createSelector(
  selectors.reservationSelector,
  selectors.currencySelector,
  currencyExchangeSelector,
  exchangeMultiplierSelector,
  (reservation, currency, currencyExchange, exchangeMultiplier) =>
    reservation &&
    reservation.getIn(["roomReservations", "0", "selectedServices"]).reduce((accum, it) => {
      return {
        ...accum,
        [it.get("ticker")]: it.get("amount").get("amount") * exchangeMultiplier,
      };
    }, {})
);

export const inventoryARISelector = createSelector(
  getModelSelector,
  selectors.propertyTickerSelector,
  selectors.reservationSelector,
  roomStaySelector,
  (model, propertyTicker, reservation, roomStay) => {
    let result = null;

    const inventoryTicker = roomStay && roomStay.get("inventoryTicker");

    if (inventoryTicker) {
      const accommodationARI =
        model &&
        model.get("accommodationARI") &&
        model.get("accommodationARI").get(propertyTicker) &&
        model.get("accommodationARI").get(propertyTicker).get("arivalues");

      if (accommodationARI) {
        result = accommodationARI.get(inventoryTicker);
      }
    }

    return result;
  }
);

export const isModificationAvailableSelector = createSelector(
  inventoryARISelector,
  inventoryARI => inventoryARI && inventoryARI.get("availability") > 0
);

export const newModificationPriceSelector = createSelector(
  inventoryARISelector,
  inventoryARI => inventoryARI && inventoryARI.get("totalRate")
);

export const checkInSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && moment(roomStay.get("checkIn"), "YYYY-MM-DD").toDate()
);

export const newCheckInSelector = createSelector(
  getModelSelector,
  checkInSelector,
  (model, currentCheckIn) => (model && model.get("checkIn")) || currentCheckIn
);

export const checkOutSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && moment(roomStay.get("checkOut"), "YYYY-MM-DD").toDate()
);

export const newCheckOutSelector = createSelector(
  getModelSelector,
  checkOutSelector,
  (model, currentCheckOut) => (model && model.get("checkOut")) || currentCheckOut
);

export const guestsSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("capacity")
);

export const mealPlanSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.getIn(["roomReservations", "0", "mealPlan"])
);

export const conditionSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.getIn(["roomReservations", "0", "condition", "name"])
);

export const conditionsSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.getIn(["roomReservations", "0", "condition"])
);

export const subTotalAmountSelector = createSelector(
  selectors.reservationSelector,
  exchangeMultiplierSelector,
  (reservation, exchangeMultiplier) =>
    reservation &&
    reservation.getIn(["roomReservations", "0", "subTotalAmount", "amount"]) * exchangeMultiplier
);

export const discountsSelector = createSelector(
  selectors.reservationSelector,
  reservation => reservation && reservation.getIn(["roomReservations", "0", "discounts"])
);

export const subTotalAmountWithDiscountsSelector = createSelector(
  subTotalAmountSelector,
  discountsSelector,
  exchangeMultiplierSelector,
  (subTotalAmount, discounts, exchangeMultiplier) =>
    subTotalAmount +
    discounts.reduce((acc, discount) => acc + discount.get("totalDiscount").get("amount"), 0) *
      exchangeMultiplier
);

export const selectedServicesSelector = createSelector(
  selectors.reservationSelector,
  reservation =>
    reservation &&
    reservation
      .getIn(["roomReservations", "0", "selectedServices"])
      .toJS()
      .reduce((accum, it) => ({ ...accum, [it.ticker]: it }), {})
);

export const totalAmountSelector = createSelector(
  selectors.reservationSelector,
  exchangeMultiplierSelector,
  (reservation, exchangeMultiplier) =>
    reservation &&
    reservation.getIn(["roomReservations", "0", "totalAmount", "amount"]) * exchangeMultiplier
);

export const accommodationTypeSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("accommodationType")
);

export const accommodationQuantitySelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("quantity")
);

export const currentInventorySelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("inventoryTicker")
);

export const inventorySelector = createSelector(
  getModelSelector,
  model => model && model.get("inventory")
);

export const isAccommodationAvailableSelector = createSelector(
  selectors.propertyTickerSelector,
  reservationARISelector,
  currentInventorySelector,
  (propertyTicker, reservationARI, currentInventory) =>
    propertyTicker &&
    reservationARI &&
    currentInventory &&
    reservationARI.get(propertyTicker) &&
    reservationARI.get(propertyTicker).get("arivalues") &&
    reservationARI.get(propertyTicker).get("arivalues").get(currentInventory) &&
    reservationARI.get(propertyTicker).get("arivalues").get(currentInventory).get("lastStatus") &&
    reservationARI
      .get(propertyTicker)
      .get("arivalues")
      .get(currentInventory)
      .get("lastStatus")
      .get("code") === constants.PROPERTY_STATUS.AVAILABLE
);

export const newInventorySelector = createSelector(
  currentInventorySelector,
  inventorySelector,
  (currentInventory, inventory) => inventory || currentInventory
);

export const numberOfUnitsSelector = createSelector(
  getModelSelector,
  model => model && model.get("numberOfUnits")
);

export const newNumberOfUnitsSelector = createSelector(
  numberOfUnitsSelector,
  accommodationQuantitySelector,
  (numberOfUnits, accommodationQuantity) => numberOfUnits || accommodationQuantity
);

function calculateServicesDateQuantity({ startDate, endDate, service }) {
  const dateQuantity = {};
  const startDateIteration = startDate.clone();
  for (startDateIteration; startDateIteration.isBefore(endDate); startDateIteration.add(1, "day")) {
    const formattedStartDate = startDateIteration.format("YYYY-MM-DD");

    if (service.get("dateQuantity")) {
      dateQuantity[formattedStartDate] =
        service.get("dateQuantity").get(formattedStartDate) ||
        service.get("dateQuantity").get(formattedStartDate) === 0
          ? service.get("dateQuantity").get(formattedStartDate)
          : service.get("quantity");
    } else {
      if (service.get("type") === constants.SERVICE_TYPE.UNIT) {
        dateQuantity[formattedStartDate] = service.get("quantity");
      } else {
        dateQuantity[formattedStartDate] = 1;
      }
    }
  }
  return dateQuantity;
}

function calculateReservationServices({ inventory, newExtras, services, startDate, endDate }) {
  const inventoryServices = inventory.get("services") || [];
  // TODO: add form values to extras
  const obligatoryServices = inventoryServices
    .filter(ticker => services.get(ticker) && services.get(ticker).get("obligatory"))
    .map(ticker => services.get(ticker))
    .map(service => ({
      ticker: service.get("ticker"),
      dateQuantity: calculateServicesDateQuantity({
        startDate,
        endDate,
        service,
      }),
    }));

  const reservationServices =
    newExtras &&
    newExtras
      .filter(service => {
        // exclude obligatories
        const ticker = service.get("ticker");
        return !(services.get(ticker) && services.get(ticker).get("obligatory"));
      })
      .filter(service => {
        const ticker = service.get("ticker");
        return Boolean(services.get(ticker)) && inventoryServices.indexOf(ticker) >= 0;
      })
      .map(service => ({
        ticker: service.get("ticker"),
        dateQuantity: calculateServicesDateQuantity({
          startDate,
          endDate,
          service,
        }),
      }));

  return [...obligatoryServices, ...reservationServices];
}

export const tempInventorySelector = createSelector(
  selectors.propertyTickerSelector,
  newInventorySelector,
  accommodationARISelector,
  selectors.defaultCurrencySelector,
  selectors.currencySelector,
  currencyExchangeSelector,
  checkInSelector,
  checkOutSelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  (
    propertyTicker,
    newInventory,
    accommodationARI,
    defaultCurrency,
    currency,
    currencyExchange,
    checkIn,
    checkOut,
    addObligatoryExtrasRateToRoomSelection
  ) => {
    if (!accommodationARI) {
      return undefined;
    }
    const inventoryData = accommodationARI.get(propertyTicker).get("inventories").get(newInventory);

    const inventoryARIValues = accommodationARI
      .get(propertyTicker)
      .get("arivalues")
      .get(newInventory);

    if (!inventoryData || !inventoryARIValues) {
      return undefined;
    }

    const propertyList = buildPropertyList(
      accommodationARI,
      accommodationARI,
      undefined,
      checkIn,
      checkOut,
      defaultCurrency,
      currency,
      currencyExchange,
      propertyTicker,
      false,
      addObligatoryExtrasRateToRoomSelection,
      undefined,
      undefined,
      0,
      undefined
    );
    return propertyList.get(0).get("inventories").get(newInventory);
  }
);

export const tempReservationAmountSelector = createSelector(
  tempInventorySelector,
  newNumberOfUnitsSelector,
  (inventory, newNumberOfUnits) =>
    inventory &&
    newNumberOfUnits &&
    inventory.get("totalRate") &&
    inventory.get("totalRate") * newNumberOfUnits
);

export const tempReservationInventoryNameSelector = createSelector(
  selectors.propertyTickerSelector,
  accommodationARISelector,
  tempInventorySelector,
  (propertyTicker, accommodationARI, inventory) => {
    if (!propertyTicker || !accommodationARI || !inventory) {
      return undefined;
    }

    const accommodationTicker = inventory.get("accommodation");

    const accommodation = accommodationARI
      .get(propertyTicker)
      .get("accommodations")
      .get(accommodationTicker);

    const accommodationName = accommodation && accommodation.get("name");

    return accommodationName && accommodationName + ", " + inventory.get("name");
  }
);

export const currentStayAmountSelector = createSelector(
  roomStaySelector,
  exchangeMultiplierSelector,
  (roomStay, exchangeMultiplier) => {
    if (!roomStay) {
      return undefined;
    }
    const totalAmount = roomStay.get("totalAmount").get("amount");

    return totalAmount * exchangeMultiplier;
  }
);

export const currentInventoryNameSelector = createSelector(roomStaySelector, roomStay => {
  if (!roomStay) {
    return undefined;
  }
  return `${roomStay.get("accommodationType")} ${roomStay.get("roomConfiguration")} ${roomStay.get(
    "mealPlan"
  )} ${roomStay.get("condition").get("name")}`;
});

export const currentOccupantsSelector = createSelector(
  roomStaySelector,
  reservationARISelector,
  selectors.propertyTickerSelector,
  (roomStay, reservationARI, propertyTicker) => {
    const defaultValue = {
      seniors: 0,
      adults: 2,
      teenagers: 0,
      children: 0,
      babies: 0,
    };
    if (!roomStay || !reservationARI) {
      return undefined;
    }

    const configurationTicker = roomStay.get("configurationTicker");

    const configuration = reservationARI
      .get(propertyTicker)
      .get("configurations")
      .get(configurationTicker);

    if (!configuration) {
      return defaultValue;
    }

    const guests = configuration.get("guests");

    return {
      seniors: guests.get(constants.OCCUPANTS_TYPE.SENIOR)
        ? guests.get(constants.OCCUPANTS_TYPE.SENIOR).get("quantity")
        : 0,
      adults: guests.get(constants.OCCUPANTS_TYPE.ADULT)
        ? guests.get(constants.OCCUPANTS_TYPE.ADULT).get("quantity")
        : 2,
      teenagers: guests.get(constants.OCCUPANTS_TYPE.TEENAGER)
        ? guests.get(constants.OCCUPANTS_TYPE.TEENAGER).get("quantity")
        : 0,
      children: guests.get(constants.OCCUPANTS_TYPE.CHILDREN)
        ? guests.get(constants.OCCUPANTS_TYPE.CHILDREN).get("quantity")
        : 0,
      babies: guests.get(constants.OCCUPANTS_TYPE.BABY)
        ? guests.get(constants.OCCUPANTS_TYPE.BABY).get("quantity")
        : 0,
      childrenCount: guests.get(CHILDREN_COUNT_FIELD)
        ? guests.get(constants.OCCUPANTS_TYPE.CHILDREN_COUNT_FIELD).get("quantity")
        : 0,
    };
  }
);

const buildImg = it => img => ({
  ...img,
  url: img.url,
  description: it.get("description"),
});

export const currentAccommodationMediaSelector = createSelector(
  roomStaySelector,
  reservationARISelector,
  selectors.propertyTickerSelector,
  (roomStay, reservationARI, propertyTicker) => {
    const defaultValue = [];
    if (!roomStay || !reservationARI) {
      return defaultValue;
    }

    const accommodationTicker = roomStay.get("accommodationTicker");

    const accommodation = reservationARI
      .get(propertyTicker)
      .get("accommodations")
      .get(accommodationTicker);

    if (!accommodation) {
      return defaultValue;
    }
    const media = accommodation.get("media") ? accommodation.get("media").toJS() : [];

    return media.map(buildImg(accommodation));
  }
);

export const numberOfNightsSelector = createSelector(
  checkInSelector,
  checkOutSelector,
  (startDate, endDate) => {
    return calculateDaysBetweenDates({ startDate, endDate });
  }
);

export const accommodationsSelector = createSelector(
  selectors.accommodationsOrderSelector,
  selectors.currencySelector,
  selectors.propertyTickerSelector,
  numberOfNightsSelector,
  accommodationARISelector,
  currencyExchangeSelector,
  (
    accommodationsOrder,
    currency,
    propertyTicker,
    numberOfNights,
    accommodationARI,
    currencyExchange
  ) => {
    if (!accommodationARI) {
      return new List();
    }
    const imgWidth = (window && window.innerWidth >= 768 && 750) || 194;
    const imgHeight = (window && window.innerWidth >= 768 && 500) || 129;
    const imgPlaceHolder = "https://placehold.it/252x159";
    const allAccommodations = accommodationARI
      .get(propertyTicker)
      .get("accommodations")
      .map(elem => {
        const accommodationAriValues = accommodationARI
          .get(propertyTicker)
          .get("arivalues")
          .get("accommodation_" + elem.get("ticker"));
        const it = transformBookableWithCurrencyConversion(
          elem.merge(accommodationAriValues),
          currency,
          currencyExchange
        );
        const media = it.get("media") ? it.get("media").toJS() : [];
        const lastStatus = it.get("lastStatus") || null;
        return {
          ticker: it.get("ticker"),
          title: it.get("name"),
          subtitle: "",
          minPrice: it.get("totalRate") / numberOfNights,
          maxPrice: it.get("totalRate") / numberOfNights,
          currency: currency,
          thumbnail:
            media && media.length > 0
              ? buildImgUrl(media[0].url, imgHeight, imgWidth, imgPlaceHolder)
              : "https://placehold.it/150x90",
          media: media && media.length > 0 ? media.map(buildImg(it)) : [],
          description: it.get("description"),
          inventories: (it.get("inventories") && it.get("inventories").toJS()) || [],
          statusCode: (lastStatus && lastStatus.get("code")) || null,
          statusValue: (lastStatus && lastStatus.get("value")) || null,
          statusFilterType: (lastStatus && lastStatus.get("filterType")) || null,
          order: it.get("order"),
        };
      })
      .toList();

    const sortProperty =
      accommodationsOrder === constants.ACCOMMODATIONS_ORDER.LIST_POSITION ? "order" : "minPrice";

    const availableAccommodations = allAccommodations
      .filter(it => it.statusCode === constants.PROPERTY_STATUS.AVAILABLE)
      .sort((itemA, itemB) => sortBy(itemA, itemB, sortProperty, false));

    const restrictedAccommodations = allAccommodations
      .filter(it => it.statusCode === constants.PROPERTY_STATUS.RESTRICTED)
      .sort((itemA, itemB) => sortBy(itemA, itemB, "order", false));

    const unavailableAccommodations = allAccommodations
      .filter(it => it.statusCode === constants.PROPERTY_STATUS.UNAVAILABLE)
      .sort((itemA, itemB) => sortBy(itemA, itemB, "order", false));

    return availableAccommodations
      .concat(restrictedAccommodations)
      .concat(unavailableAccommodations);
  }
);

export const inventoriesSelector = createSelector(
  selectors.propertyTickerSelector,
  accommodationARISelector,
  selectors.defaultCurrencySelector,
  selectors.currencySelector,
  selectors.roomDenominationSelector,
  numberOfNightsSelector,
  accommodationSelector,
  currencyExchangeSelector,
  roomStaySelector,
  checkInSelector,
  checkOutSelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  (
    propertyTicker,
    accommodationARI,
    defaultCurrency,
    currency,
    roomDenomination,
    numberOfNights,
    accommodation,
    currencyExchange,
    roomStay,
    checkIn,
    checkOut,
    addObligatoryExtrasRateToRoomSelection
  ) => {
    if (!accommodation) {
      return undefined;
    }
    const inventoryList = new List(
      accommodation.inventories
        .map(it => {
          const property = accommodationARI.get(propertyTicker);
          const inventoryARIValues = accommodationARI.get(propertyTicker).get("arivalues").get(it);

          // Inventory not found in this ARI

          if (!inventoryARIValues) {
            return null;
          }

          const accommodationRoomDenomination = accommodation.roomDenomination;

          const propertyList = buildPropertyList(
            accommodationARI,
            accommodationARI,
            undefined,
            checkIn,
            checkOut,
            defaultCurrency,
            currency,
            currencyExchange,
            propertyTicker,
            false,
            addObligatoryExtrasRateToRoomSelection,
            undefined,
            undefined,
            0,
            undefined
          );
          const inventory = propertyList.get(0).get("inventories").get(it);
          if (!inventory) {
            return null;
          }

          const lastStatus = inventory.get("lastStatus") || null;
          let discountAmount = inventory.get("discountAmount");
          let discountPercentage = inventory.get("discountPercentage");
          if (inventory.get("rackRate") > 0) {
            discountAmount = inventory.get("rackRate") - inventory.get("totalRate");
            discountAmount = (discountAmount > 0 && discountAmount) || null;
            discountPercentage = 1 - inventory.get("totalRate") / inventory.get("rackRate");
          }
          const services = accommodationARI.get(propertyTicker).get("services");
          const inventoryServices = inventory.get("services") || [];
          const obligatoryServices = inventoryServices.reduce((accumulator, ticker) => {
            const serviceExistsAndIsObligatory =
              services.get(ticker) && services.get(ticker).get("obligatory");

            return serviceExistsAndIsObligatory
              ? { ...accumulator, [ticker]: { quantity: 1 } }
              : accumulator;
          }, {});

          const currentReservationServices = roomStay.get("selectedServices");

          const reservationServices = currentReservationServices.reduce((accumulator, service) => {
            const ticker = service.get("ticker");
            const serviceExistsAndIsInInventory =
              Boolean(services.get(ticker)) && inventoryServices.indexOf(ticker) >= 0;

            return serviceExistsAndIsInInventory
              ? { ...accumulator, [ticker]: { quantity: 1 } }
              : accumulator;
          }, {});

          const selectedServices = {
            ...obligatoryServices,
            ...reservationServices,
          };

          const accommodationName = property
            .get("accommodations")
            .get(inventory.get("accommodation"))
            .get("name");

          const occupationName = property
            .get("configurations")
            .get(inventory.get("configuration"))
            .get("name");

          const mealPlanName = property.get("mealplans").get(inventory.get("mealplan")).get("name");

          const conditionName = property
            .get("conditions")
            .get(inventory.get("condition"))
            .get("name");

          return new Map({
            ticker: it,
            name: `${accommodationName} ${occupationName} ${mealPlanName} ${conditionName}`,
            accommodation: property.get("accommodations").get(inventory.get("accommodation")),
            mealplan: property.get("mealplans").get(inventory.get("mealplan")),
            condition: property.get("conditions").get(inventory.get("condition")),
            rackRate: inventory.get("rackRate") || null,
            paymentTypes:
              property.get("conditions").get(inventory.get("condition")).get("paymentTypes") ||
              null,
            discountPercentage: discountPercentage || null,
            discountAmount: discountAmount || null,
            originalRate: inventory.get("originalRate") || null,
            averageRate: inventory.get("averageRate") || null,
            totalRate: inventory.get("totalRate") || null,
            availability: inventory.get("availability") || null,
            configuration: property.get("configurations").get(inventory.get("configuration")),
            statusCode: (lastStatus && lastStatus.get("code")) || null,
            statusValue: (lastStatus && lastStatus.get("value")) || null,
            statusFilterType: (lastStatus && lastStatus.get("filterType")) || null,
            originalRates: inventory.get("originalRates"),
            services: inventory.get("services") || null,
            obligatoryServices,
            selectedServices: selectedServices,
            roomDenomination:
              accommodationRoomDenomination ||
              roomDenomination ||
              constants.ROOM_DENOMINATION.UNITS,
          });
        })
        .filter(it => it !== null)
        .sort((itemA, itemB) => sortBy(itemA, itemB, "totalRate", false))
    );

    return new Map().set(propertyTicker, inventoryList);
  }
);

// "AMOUNT_AND_STAY_GTE"
// "STAY_GTE"
// "AMOUNT_GTE"
// "NO_RESTRICTION"
export const datesModificationRestrictionTypeSelector = createSelector(
  modificationPolicySelector,
  modificationPolicy =>
    (modificationPolicy && modificationPolicy.get("datesModificationRestrictionType")) ||
    constants.DATES_MODIFICATION_RESTRICTION_TYPE.NO_RESTRICTION
);

// "AMOUNT_GTE"
// "NO_RESTRICTION"
export const accommodationModificationRestrictionTypeSelector = createSelector(
  modificationPolicySelector,
  modificationPolicy =>
    (modificationPolicy && modificationPolicy.get("accommodationModificationRestrictionType")) ||
    constants.ACCOMMODATION_MODIFICATION_RESTRICTION_TYPE.NO_RESTRICTION
);

export const accommodationsModificationReleaseSelector = createSelector(
  modificationPolicySelector,
  modificationPolicy =>
    (modificationPolicy &&
      modificationPolicy.get("accommodationMinRelease") &&
      modificationPolicy.get("accommodationMinRelease").get("duration")) ||
    0
);

export const datesModificationReleaseSelector = createSelector(
  modificationPolicySelector,
  modificationPolicy =>
    (modificationPolicy &&
      modificationPolicy.get("datesMinRelease") &&
      modificationPolicy.get("datesMinRelease").get("duration")) ||
    0
);

// can dates be modified?
export const areReservationDatesModifiableSelector = createSelector(
  datesModificationReleaseSelector,
  checkInSelector,
  modificationPolicySelector,
  (release, checkIn, modificationPolicy) => {
    if (!modificationPolicy) {
      return { dateOfExpiration: null, isModifiable: false };
    }
    const entry = new Date(checkIn.getTime());
    let dateOfExpiration;
    let isModifiable;

    dateOfExpiration = entry.setDate(entry.getDate() - release);
    isModifiable = dateOfExpiration >= Date.now() && modificationPolicy.get("canModifyDates");
    return { dateOfExpiration, isModifiable };
  }
);

// can accommodations be modified?
export const isReservationAccommodationModifiableSelector = createSelector(
  accommodationsModificationReleaseSelector,
  checkInSelector,
  modificationPolicySelector,
  (release, checkIn, modificationPolicy) => {
    if (!modificationPolicy) {
      return { dateOfExpiration: null, isModifiable: false };
    }
    const entry = new Date(checkIn.getTime());
    let dateOfExpiration;
    let isModifiable;
    dateOfExpiration = entry.setDate(entry.getDate() - release);
    isModifiable =
      dateOfExpiration >= Date.now() && modificationPolicy.get("canModifyAccommodations");
    return { dateOfExpiration, isModifiable };
  }
);

// can extras be modified?
export const areReservationExtrasModifiableSelector = createSelector(
  areReservationDatesModifiableSelector,
  isReservationAccommodationModifiableSelector,
  isAccommodationAvailableSelector,
  (
    areReservationDatesModifiable,
    isReservationAccommodationModifiable,
    isAccommodationAvailable
  ) => ({
    dateOfExpiration: new Date(
      Math.max(
        areReservationDatesModifiable.dateOfExpiration,
        isReservationAccommodationModifiable.dateOfExpiration
      )
    ),
    // So undefined would still render or else there's a noticeable lag
    isModifiable: isAccommodationAvailable === false ? false : true,
  })
);

// can anything be modified?
export const isReservationModifiableSelector = createSelector(
  areReservationDatesModifiableSelector,
  isReservationAccommodationModifiableSelector,
  (areReservationDatesModifiable, isReservationAccommodationModifiable) =>
    areReservationDatesModifiable.isModifiable || isReservationAccommodationModifiable.isModifiable
);

export const ageSelectorFieldsSelector = createSelector(
  bookingFormFieldsSelectorInput,
  childrenCountSelector,
  occupantRestrictionSelector,
  occupantsSelectorRangeSelector,
  (bookingFormFieldsInput, childrenCount, occupantRestriction, occupantsSelectorRange) => {
    if (!bookingFormFieldsInput) {
      return null;
    }
    const { occupantsMaxValues, occupantsMinAge, occupantsFilterType } = bookingFormFieldsInput;

    const showChildrenCount = showChildrenCounter(occupantsMaxValues, occupantsFilterType);
    const childrenFields = childrenCount
      ? range(childrenCount).reduce((previous, idx) => ({ ...previous, [`age${idx}`]: true }), {})
      : [];
    const fields = {
      childrenCount: showChildrenCount,
      ...childrenFields,
    };

    switch (occupantRestriction) {
      case constants.OCCUPANT_RESTRICTION.TAKE_ALL_PEOPLE:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.TAKE_SENIOR_ADULT_TEENAGER_CHILD_BABY:
        fields[constants.OCCUPANTS_TYPE.SENIOR] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.SENIOR
        );
        fields[constants.OCCUPANTS_TYPE.ADULT] = occupantsMaxValues.has(
          constants.OCCUPANTS_TYPE.ADULT
        );
        fields[constants.OCCUPANTS_TYPE.TEENAGER] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.TEENAGER);
        fields[constants.OCCUPANTS_TYPE.CHILDREN] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.CHILDREN);
        fields[constants.OCCUPANTS_TYPE.BABY] =
          !showChildrenCount && occupantsMaxValues.has(constants.OCCUPANTS_TYPE.BABY);
        break;

      case constants.OCCUPANT_RESTRICTION.NONE:
      default:
        break;
    }

    const selectorsRange = calculateSelectorsRange({
      showChildrenCount: showChildrenCount,
      occupantsMinAge,
      occupantsMaxValues,
      occupantsSelectorRange,
      fields,
    });
    const ageRanges = bookingFormFieldsInput.ageRanges && bookingFormFieldsInput.ageRanges.toJS();
    return { fields, selectorsRange, ageRanges };
  }
);

export const extrasSelector = createSelector(
  selectors.propertyTickerSelector,
  selectors.currencySelector,
  currentInventorySelector,
  accommodationARISelector,
  currencyExchangeSelector,
  (propertyTicker, currency, currentInventoryTicker, accommodationARI, currencyExchange) => {
    if (!accommodationARI) {
      return [];
    }

    const inventory = accommodationARI
      .get(propertyTicker)
      .get("inventories")
      .get(currentInventoryTicker);

    if (!inventory || !inventory.get("services")) {
      return [];
    }

    const sortedServices = accommodationARI
      .get(propertyTicker)
      .get("services")
      .filter(el => {
        const availabilityPerDay =
          accommodationARI.get(propertyTicker) &&
          accommodationARI.get(propertyTicker).get("arivalues").get(el.get("ticker")) &&
          accommodationARI
            .get(propertyTicker)
            .get("arivalues")
            .get(el.get("ticker"))
            .get("availabilityPerDay");
        return Boolean(!availabilityPerDay);
      })
      .filter(it => inventory.get("services").indexOf(it.get("ticker")) >= 0)
      .sortBy(service => service.get("order"))
      .map(service => {
        const serviceARIValues = accommodationARI
          .get(propertyTicker)
          .get("arivalues")
          .get(service.get("ticker"));
        return transformBookableWithCurrencyConversion(
          service.merge(serviceARIValues),
          currency,
          currencyExchange
        );
      });

    const services = [];

    sortedServices.forEach(el => {
      const media = el.get("media") ? el.get("media").toJS() : [];
      const entity = {
        ticker: el.get("ticker"),
        title: el.get("name"),
        minPrice: el.get("totalRate"),
        maxPrice: el.get("totalRate"),
        unit: el.get("type"),
        type: el.get("type"),
        obligatory: el.get("obligatory"),
        percentage: el.get("type") === constants.SERVICE_TYPE.PERCENTAGE,
        currency: currency,
        showFooter: false,
        showInfo: true,
        hasCheckBox: el.get("type") !== constants.SERVICE_TYPE.UNIT,
        thumbnail: media && media.length > 0 ? media[0].url : undefined,
        hasImage: media && media.length > 0,
        media: media && media.length > 0 ? media.map(buildImg(el)) : [],
        description: el.get("description"),
        statusCode: constants.BOOKABLE_STATUS.AVAILABLE,
        selectOptions:
          el.get("type") === constants.SERVICE_TYPE.UNIT &&
          new Array(el.get("maxUnits")).fill(false).map((ign, idx) => ({
            key: idx + 1,
            value: idx + 1,
          })),
        dateSelectionType: el.get("dateSelectionType"),
        totalRatePerDay: accommodationARI
          .get(propertyTicker)
          .get("arivalues")
          .get(el.get("ticker"))
          .get("totalRatePerDay"),
        appliesToGuests: el.get("appliesToGuests"),
      };

      services.push(entity);
    });

    return services;
  }
);

export const extrasQuantitySelector = createSelector(
  getModelSelector,
  model => model && model.get("extrasQuantity")
);

export const extrasFormsSelector = createSelector(
  getModelSelector,
  model => model && model.get("extrasForms")
);

export const currentExtrasSelector = createSelector(
  roomStaySelector,
  roomStay => roomStay && roomStay.get("selectedServices")
);

export const totalOccupantsSelector = createSelector(
  currentOccupantsSelector,
  currentOccupants =>
    (currentOccupants &&
      Object.keys(currentOccupants).reduce(
        (accumulator, it) => accumulator + currentOccupants[it],
        0
      )) ||
    0
);

export const selectedExtrasSelector = createSelector(
  extrasQuantitySelector,
  selectors.propertyTickerSelector,
  accommodationARISelector,
  accommodationQuantitySelector,
  numberOfNightsSelector,
  currentStayAmountSelector,
  selectors.defaultCurrencySelector,
  selectors.currencySelector,
  currencyExchangeSelector,
  tempInventorySelector,
  (
    extrasQuantity,
    propertyTicker,
    accommodationARI,
    accommodationQuantity,
    numberOfNights,
    currentStayAmount,
    defaultCurrency,
    currency,
    currencyExchange,
    tempInventory
  ) => {
    if (!extrasQuantity || !accommodationARI || !tempInventory) {
      return undefined;
    }

    const extras = accommodationARI
      .get(propertyTicker)
      .get("services")
      .map(service => {
        const serviceARIValues = accommodationARI
          .get(propertyTicker)
          .get("arivalues")
          .get(service.get("ticker"));
        return transformBookableWithCurrencyConversion(
          service.merge(serviceARIValues),
          currency,
          currencyExchange
        );
      });

    return fromJS(
      Object.keys(extrasQuantity)
        .filter(
          extraTicker =>
            extrasQuantity[extraTicker] &&
            Object.values(extrasQuantity[extraTicker]).some(quantity => quantity > 0)
        )
        .map(extraTicker => {
          const extra = extras.find(it => it.get("ticker") === extraTicker);
          return {
            ticker: extraTicker,
            dateQuantity: extrasQuantity[extraTicker],
            name: extra.get("name"),
            amount: {
              amount: calculateServiceRate({
                service: extra.set("dateQuantity", extrasQuantity[extraTicker]),
                rooms: accommodationQuantity,
                transformCurrency: false,
                stayRate: currentStayAmount,
                inventoryTicker: tempInventory.get("ticker"),
                defaultCurrency,
                currencyExchange,
                propertyARI: accommodationARI && accommodationARI.get(propertyTicker),
              }),
            },
            percentage: extra.get("type") === constants.SERVICE_TYPE.PERCENTAGE,
            obligatory: extra.get("obligatory"),
          };
        })
    );
  }
);

export const newExtrasSelector = createSelector(
  currentExtrasSelector,
  selectedExtrasSelector,
  (currentExtras, selectedExtras) => selectedExtras || currentExtras
);

export const extrasComparisonInfoSelector = createSelector(
  currentExtrasSelector,
  selectedExtrasSelector,
  (currentExtras, selectedExtras) => {
    if (!selectedExtras) {
      return {
        currentExtras: currentExtras.toJS(),
        newExtras: [],
      };
    }
    return {
      currentExtras: currentExtras.toJS(),
      newExtras: selectedExtras.toJS(),
    };
  }
);

export const currentExtrasAmountSelector = createSelector(
  currentExtrasSelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  exchangeMultiplierSelector,
  (currentExtras, addObligatoryExtrasRateToRoomSelection, exchangeMultiplier) => {
    return (
      ((currentExtras &&
        currentExtras.reduce(
          (accumulator, extra) =>
            (extra.get("obligatory") && addObligatoryExtrasRateToRoomSelection) ||
            extra.get("amount").get("amount") < 0
              ? accumulator
              : accumulator + extra.get("amount").get("amount"),
          0
        )) ||
        0) * exchangeMultiplier
    );
  }
);

export const newExtrasAmountSelector = createSelector(
  selectedExtrasSelector,
  selectors.addObligatoryExtrasRateToRoomSelectionSelector,
  exchangeMultiplierSelector,
  (selectedExtras, addObligatoryExtrasRateToRoomSelection, exchangeMultiplier) => {
    return (
      ((selectedExtras &&
        selectedExtras.reduce(
          (accumulator, extra) =>
            (extra.get("obligatory") && addObligatoryExtrasRateToRoomSelection) ||
            extra.get("amount").get("amount") < 0
              ? accumulator
              : accumulator + extra.get("amount").get("amount"),
          0
        )) ||
        0) * exchangeMultiplier
    );
  }
);

export const tempReservationSelector = createSelector(
  selectors.propertyTickerSelector,
  newNumberOfUnitsSelector,
  accommodationARISelector,
  roomStayIdSelector,
  newCheckInSelector,
  newCheckOutSelector,
  selectors.currencySelector,
  selectors.reservationSelector,
  tempInventorySelector,
  newExtrasSelector,
  roomStaySelector,
  extrasFormsSelector,
  deviceInfoSelector,
  selectors.ipInfoSelector,
  (
    propertyTicker,
    newNumberOfUnits,
    accommodationARI,
    roomStayId,
    newCheckIn,
    newCheckOut,
    currency,
    reservation,
    inventory,
    newExtras,
    roomStay,
    extrasForm,
    deviceInfo,
    ipInfo
  ) => {
    if (!accommodationARI || !inventory) {
      return undefined;
    }

    const services = accommodationARI.get(propertyTicker).get("services");

    const result = {
      reservationId: roomStayId,
      hotelTicker: propertyTicker,
      promoCodes: reservation.get("promoCodes"),
      language: reservation.get("language"),
      referrer: null,
      affiliate: reservation.get("channelId"),
    };

    if (reservation) {
      result.deviceInfo = deviceInfo.toJS();

      result.customer = reservation.get("customer") && reservation.get("customer").toJS();

      result.paymentData = {
        paymentType: reservation.get("paymentTicker"),
        creditCard: {
          holderName: "-",
          type: "Visa",
          number: "4111111111111111",
          securityCode: null,
          expiringMonth: "12",
          expiringYear: "25",
        },
      };

      result.locationInfo = {
        countryCode: ipInfo.get("country"),
        cityName: ipInfo.get("cityName"),
      };

      if (roomStay) {
        result.comments = roomStay.get("comments");

        if (extrasForm) {
          Object.keys(extrasForm).forEach(extraFormKey => {
            Object.keys(extrasForm[extraFormKey]).forEach(() => {
              const newExtra = newExtras && newExtras.toJS().find(it => it.ticker === extraFormKey);
              const extraName = newExtra && newExtra.name;

              result.comments += `\n${extraName}${customFormObjectToString(
                extrasForm[extraFormKey]
              )}`;
            });
          });
        }

        result.reservations = [
          {
            idConfirmation: roomStay.get("confirmationId"),
            ticker: inventory.get("ticker"),
            totalRate: {
              currency,
              amount: inventory.get("totalRate"),
            },
            startDate: moment(newCheckIn).format("YYYY-MM-DD"),
            endDate: moment(newCheckOut).format("YYYY-MM-DD"),
            quantity: newNumberOfUnits,
            selectedServices: calculateReservationServices({
              inventory,
              newExtras,
              services,
              startDate: moment(newCheckIn),
              endDate: moment(newCheckOut),
            }),
            additionalRequests: roomStay.get("additionalRequests"),
            guestAges: roomStay.get("guestAges"),
          },
        ];
      }
    }

    return result;
  }
);

export const tempReservationExtrasSelector = createSelector(
  tempReservationSelector,
  accommodationARISelector,
  selectors.propertyTickerSelector,
  (tempReservation, accommodationARI, propertyTicker) => {
    if (!tempReservation || !accommodationARI) {
      return undefined;
    }
    const selectedServicesTickers =
      tempReservation.reservations[0].selectedServices &&
      tempReservation.reservations[0].selectedServices.map(service => service.ticker);
    return accommodationARI
      .get(propertyTicker)
      .get("services")
      .filter(service => selectedServicesTickers.indexOf(service.get("ticker")) >= 0)
      .filter(service => service.get("obligatory"))
      .map(service => service.get("name"))
      .sort()
      .join(", ");
  }
);

export const cancellationRestrictionsModificationSelector = createSelector(
  selectors.reservationSelector,
  reservation =>
    reservation &&
    reservation.getIn(["roomReservations", "0", "cancellationPolicy", "configurations"]).toJS()
);

export const CancellationPoliciesInfoModificationSelector = createSelector(
  selectors.reservationSelector,
  reservation =>
    reservation && reservation.getIn(["roomReservations", "0", "cancellationPolicy", "description"])
);

export const guestsListSelector = createSelector(
  reservationARISelector,
  propertyTickerSelector,
  (reservationARI, propertyTicker) => {
    const result = {};
    if (
      reservationARI &&
      reservationARI.get(propertyTicker) &&
      reservationARI.get(propertyTicker).get("occupants")
    ) {
      for (const occupant in constants.CONFIGURATION_OCCUPANTS) {
        if (constants.CONFIGURATION_OCCUPANTS.hasOwnProperty(occupant)) {
          result[occupant] = parseInt(
            reservationARI.get(propertyTicker).get("occupants").get(occupant),
            10
          );
        }
      }
    }
    return result;
  }
);
